import { useState, useEffect } from 'react';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

export const useAuth = () => {
  const [userId, setUserId] = useState(null);
  const [userType, setUserType] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [userPassword, setUserPassword] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [confirmRegisterPassword, setConfirmRegisterPassword] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [registrationInProgress, setRegistrationInProgress] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  
  // Password reset states
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetCooldown, setResetCooldown] = useState(0);
  const [resetToken, setResetToken] = useState(null);
  const [showNewPasswordForm, setShowNewPasswordForm] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  // Cooldown timer effect
  useEffect(() => {
    let interval;
    if (resetCooldown > 0) {
      interval = setInterval(() => {
        setResetCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resetCooldown]);

  // Check for reset token in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("reset_token");
    if (token) {
      setResetToken(token);
      setShowForgotPassword(true);
      setShowNewPasswordForm(true);
    }
  }, []);

  const login = async () => {
    setIsSending(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/init_chat`, {
        user_name: userName,
        user_password: userPassword,
      });

      // Redirect to Stripe if needed
      if (response.data.redirect_to_stripe) {
        const stripe_session_id = response.data.sessionId;
        localStorage.setItem('stripe_session_id', stripe_session_id);
        localStorage.setItem('payment_email', userName);

        const stripe = await stripePromise;
        await stripe.redirectToCheckout({ sessionId: response.data.sessionId });
        return { success: false };
      }

      if (response.data.error) {
        setErrorMessage(response.data.error);
        return { success: false };
      }

      setErrorMessage('');
      return { success: true, data: response.data };
    } catch (error) {
      setErrorMessage(error.response?.data?.error || 'Login failed');
      return { success: false };
    } finally {
      setIsSending(false);
    }
  };

  const register = async () => {
    if (registrationInProgress) return;
    setRegistrationInProgress(true);
    setErrorMessage('');

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/register`, {
        first_name: firstName,
        last_name: lastName,
        email: email,
        password: registerPassword,
        confirm_password: confirmRegisterPassword
      });

      setErrorMessage("To complete registration, please check your email and click the verification link. You can close this tab.");
      return { success: true };
    } catch (err) {
      setErrorMessage(err.response?.data?.error || 'Registration failed');
      setRegistrationInProgress(false);
      return { success: false };
    }
  };

  const resetPassword = async () => {
    if (!resetEmail) {
      setErrorMessage("Please enter your email address.");
      return;
    }

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/send_reset_email`, {
        email: resetEmail,
      });

      if (response.data.success) {
        setErrorMessage("A reset link has been sent if the email address is registered. You can close this tab.");
        setResetCooldown(15);
      } else {
        setErrorMessage(response.data.error);
        setResetCooldown(15);
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.error || 'Reset failed');
    }
  };

  const submitNewPassword = async () => {
    if (!newPassword || !confirmNewPassword) {
      setErrorMessage("Please fill in all fields.");
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setErrorMessage("Passwords do not match.");
      return;
    }

    setIsSending(true);

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/reset_password`, {
        token: resetToken,
        new_password: newPassword,
      });

      if (response.data.success) {
        setErrorMessage("Password reset. Redirecting to log in ...");
        setResetToken(null);
        setResetEmail("");
        setUserName("");
        setUserPassword("");

        setTimeout(() => {
          window.location.href = '/';
          setIsSending(false);
        }, 3500);
      } else {
        setErrorMessage(response.data.error || "Reset failed.");
      }
    } catch (error) {
      setErrorMessage("Error resetting password.");
    }
  };

  const signOut = () => {
    setUserId(null);
    setUserType(null);
    setUserName('');
    setUserPassword('');
    setFirstName('');
    setLastName('');
    setEmail('');
    setErrorMessage('');
  };

  return {
    // State
    userId, setUserId,
    userType, setUserType,
    firstName, setFirstName,
    lastName, setLastName,
    email, setEmail,
    userName, setUserName,
    userPassword, setUserPassword,
    registerPassword, setRegisterPassword,
    confirmRegisterPassword, setConfirmRegisterPassword,
    isSending,
    errorMessage, setErrorMessage,
    registrationInProgress,
    showRegister, setShowRegister,
    agreedToTerms, setAgreedToTerms,
    showForgotPassword, setShowForgotPassword,
    resetEmail, setResetEmail,
    resetCooldown,
    resetToken,
    showNewPasswordForm, setShowNewPasswordForm,
    newPassword, setNewPassword,
    confirmNewPassword, setConfirmNewPassword,
    
    // Methods
    login,
    register,
    resetPassword,
    submitNewPassword,
    signOut
  };
};
